import type * as Theme from '#theme'
import { computed, inject, toValue, useCoreConfig } from '#imports'
import type { ComputedRef, InjectionKey, MaybeRefOrGetter } from '#imports'

export const THEME_SYMBOL = Symbol() as InjectionKey<{
    theme: ComputedRef<Theme.Theme>
}>

export const useThemeService = () => {
    const { defaultTheme, themes } = useCoreConfig().theme

    function getComputedTheme(theme?: MaybeRefOrGetter<Theme.Name | undefined>): ComputedRef<Theme.Theme> {
        const injectedTheme = inject(THEME_SYMBOL, undefined)

        return computed(() => {
            if (!theme) {
                if (injectedTheme) return injectedTheme.theme.value

                theme = defaultTheme
            }

            let themeRaw = toValue(theme)

            if (!themeRaw) {
                if (injectedTheme) return injectedTheme.theme.value

                themeRaw = defaultTheme
            }

            let themeConfig: Theme.Theme | undefined = undefined

            if (typeof themeRaw === 'string') {
                const resolvedTheme = themes[themeRaw]

                if (!resolvedTheme) {
                    throw new Error(`Theme ${themeRaw} does not exist.`)
                }

                themeConfig = resolvedTheme
            }

            if (!themeConfig) {
                throw new Error(`Theme ${themeRaw} does not exist.`)
            }

            return themeConfig
        })
    }

    function getPropsForColor(options: { color: Theme.ColorBaseToken }): Partial<Record<Theme.ColorBaseToken, true>> {
        const { color } = options

        return {
            [`${color}`]: true
        } as Record<Theme.ColorBaseToken, true>
    }

    return {
        getComputedTheme,
        getPropsForColor
    }
}

export const useTheme = (options: MaybeRefOrGetter<{
    color?: Theme.ColorBaseToken | false
    theme?: Theme.Name
}> = {}) => {
    const service = useThemeService()

    const theme = service.getComputedTheme(() => toValue(options)?.theme)

    return {
        theme,
        themeClass: computed(() => {
            const optionsRaw = toValue(options)

            if (!optionsRaw.color) return {}
            if (typeof optionsRaw.color === 'string' && !(optionsRaw.color in theme.value.colors)) return {}

            return service.getPropsForColor(optionsRaw as { color: Theme.ColorBaseToken })
        })
    }
}
